import { Injectable } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ContentService } from '@blk/cwp-ng-framework';
import { ModelsTransformUtil } from '@ddt-web/common';
import { InjectConfig, LocaleUtilResolverService, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { CellData, ComponentData, DateFormatter, DisplayDataFactoryService, TabData, TableData } from '@ddt-web/data-service';
import { DialogService } from '@ddt-web/dialog';
import { ExcelGenerateConfig, SharedApplicationConstants } from '@ddt-web/domain';
import { ComponentDataTransformerService, ExcelGenerateService } from '@ddt-web/excel-generate';
import { DateUtil } from '@ddt-web/generic-utils';
import { StoreService } from '@ddt-web/store-service';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';

import { ModelsBrowseStoreConfig } from '../config/store.config';
import { FamilyTypes } from '../enums/family-types.enum';
import { FamiliesUtil } from '../utils';
import { ModelsBrowseDataService } from './models-browse-data.service';

@Injectable({
	providedIn: 'root'
})
export class ModelsHoldingsExcelDownloadService {
	@InjectConfig(ModelsExperienceConfigKey.DATE_FORMAT) private readonly dateFormat: string = DateUtil.MODELS_US_DATE_FORMAT;

	public loadingDialog: MatDialogRef<any, any>;
	private EXCEL_GENERATE_CONFIG: ExcelGenerateConfig;
	private allSheetNames: Map<string, boolean>;

	constructor(
		private dialogService: DialogService,
		private modelsBrowseDataService: ModelsBrowseDataService,
		private storeService: StoreService,
		private displayDataFactoryService: DisplayDataFactoryService,
		private contentService: ContentService,
		private excelGenerateService: ExcelGenerateService,
		private componentDataTransformerService: ComponentDataTransformerService,
		private localeUtilResolverService: LocaleUtilResolverService
	) { }


	public downloadHoldings(familyId?: string, sortByLatestTradeDate: boolean = false, familyType: string = FamilyTypes.ALL): Observable<boolean> {
		this.dialogService.showLoading();
		this.allSheetNames = new Map();
		this.EXCEL_GENERATE_CONFIG = this.localeUtilResolverService.getResolvedLocaleValue() === 'au' ? ExcelGenerateConfig.AU_MVAL : ExcelGenerateConfig.MVAL;
		return combineLatest([
			this.storeService.selectAll(ModelsBrowseStoreConfig.families),
			this.modelsBrowseDataService.fetchFamilyHoldingsForAllModels()
		]).pipe(
			first(),
			switchMap(([families, modelPortfolios]) => {
				const { familyModelsPerCategory, categories } = FamiliesUtil.generateCategoriesModelLevel(families);
				let blkSortedFamilyIds: string[];
				let cmsSortedFamilyIds: string[];
				categories.forEach(category => {
					const familyList = Object.keys(familyModelsPerCategory[category]).map(fid => families.entities[fid])
						.map(family => {
							return {
								id: family.id,
								asOfDate: new DateFormatter(this.dateFormat).format(family.asOfDate),
								displayOrder: family.displayOrder,
								subType: family.subType,
								tradeFrequency: family.tradeFrequency
							};
						});
					const sortingParams = {
						asOfDate: 'asOfDate',
						displayOrder: 'displayOrder',
						subType: 'subType',
						tradeFrequency: 'tradeFrequency'
					};
					const sortedFamilyIds = ModelsTransformUtil.sortFamiliesWithTradeDateOrFrequency(familyList, category, sortingParams, sortByLatestTradeDate).map(family => family.id);
					(category === SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP) ? blkSortedFamilyIds = sortedFamilyIds : cmsSortedFamilyIds = sortedFamilyIds;
				});
				const holdingsComponentDataId = this.localeUtilResolverService.getResolvedLocaleValue() === 'au' ? 'me-excel-holdings-details-au' : 'me-excel-holdings-details';
				const modelComponentData: Observable<ComponentData> =
					this.displayDataFactoryService.getDisplayableComponentData('me-excel-holdings-model-data', of(<string[]>families.ids), this.contentService);
				const holdingsComponentData: Observable<ComponentData> =
					this.displayDataFactoryService.getDisplayableComponentData(holdingsComponentDataId, of(modelPortfolios.map(model => model.id)), this.contentService);
				return combineLatest([modelComponentData, holdingsComponentData]).pipe(
					first(([modelData, holdingsData]) => !modelData?.isLoading && !holdingsData?.isLoading),
					switchMap(([completedModelData, completedHoldingsData]) => {
						/**
						 * Desired Final result:
						 * N-number of table = families
						 * N-number of rows = each security in all models in the family
						 * N-number of columns = family meta data + holdings data
						 *
						 * Current Data:
						 * modelData has
						 * N-number of table = families
						 * N-number of rows = models in the family
						 * N-number of columns = family meta data
						 *
						 * holdingsData has
						 * N-number of tables = all models in all families
						 * N-number of rows = securities in each model
						 * N-number of columns = holdings data
						 */
						let completeModelTableData = [];
						let cmsTableData = completedModelData.getTabById('excel-cms-holdings-model-data').tableData;
						// filter family with no models in CMS tab
						cmsTableData = cmsTableData.filter(table => table['rows'].length);
						cmsTableData.sort((a, b) => cmsSortedFamilyIds.indexOf(a.portfolioData[0].formattedValue) - cmsSortedFamilyIds.indexOf(b.portfolioData[0].formattedValue));

						this.setFamilyName(cmsTableData);


						let blkTableData = completedModelData.getTabById('excel-blk-holdings-model-data').tableData;
						// filter family with no models in BLK tab
						blkTableData = blkTableData.filter(table => table['rows'].length);
						blkTableData.sort((a, b) => blkSortedFamilyIds.indexOf(a.portfolioData[0].formattedValue) - blkSortedFamilyIds.indexOf(b.portfolioData[0].formattedValue));

						if (familyType === FamilyTypes.CMS) {
							completeModelTableData = cmsTableData;
						} else if (familyType === FamilyTypes.BLK) {
							completeModelTableData = blkTableData;
						} // Merge CMS and BLK Model Data
						else {
							completeModelTableData = [...cmsTableData, ...blkTableData];
						}
						// sort family by display order
						if (familyId) {
							completeModelTableData = this.sortFamilyTableData(completeModelTableData, familyId);
						}

						/**
						 * Append modelData into holdingsData for each model AND
						 * Group model in the same family together in one table data
						 */
						const groupedModelByFamily: CellData[][][] = [];
						completeModelTableData.forEach(familyTableData => {
							const currentFamilyCells: CellData[][] = [];
							const familyExcelSheetName: string = this.getFamilyExcelSheetName(familyTableData.cells);
							familyTableData['rows'].forEach((model, modelIndex) => {
								/**
								* After the following block: Append modelData into holdingsData for each model
								* holdingsData now has
								* N-number of tables = all models in all families
								* N-number of rows = securities in each model
								* N-number of columns = family metadata + holdings data
								*/
								const index = completedHoldingsData.portfolioData.findIndex(holdingModel => holdingModel[0].rawValue === model['id'].rawValue);
								if (index > -1) {
									completedHoldingsData.tabs[0].tableData[index]['cells' as any] =
										this.combinedCellsData(
											familyTableData.cells[modelIndex],
											completedHoldingsData.tabs[0].tableData[index].cells,
											familyExcelSheetName);
									currentFamilyCells.push(...completedHoldingsData.tabs[0].tableData[index].cells);
								}
							});
							groupedModelByFamily.push(currentFamilyCells);
						});
						/**
						* After the following block: Group model in the same family together in one table data
						* finalResultComponentData now has
						* N-number of table = families
						* N-number of rows = each security in all models in the family
						* N-number of columns = family meta data + holdings data
						* And we are done
						*/
						const finalResultComponentData = new ComponentData(
							'mb-excel-holdings-details',
							[new TabData(completedHoldingsData.tabs[0].config,
								groupedModelByFamily.filter(familyCells => familyCells.length).map(familyCells => new TableData([], [], familyCells))
							)],
							completedHoldingsData.portfolioData,
							false,
							null,
							true
						);
						return this.excelGenerateService.generateExcelFromTransformedData(
							this.EXCEL_GENERATE_CONFIG,
							// no sorting, the data is sorted manually above
							this.componentDataTransformerService.transformComponentData([finalResultComponentData], null),
							this.contentService.get('MODEL_HOLDINGS_EXCEL_REPORT_NAME', 'model-portfolio-allocations-and-turnover')
						);
					}),
					map(reportDownload => {
						this.dialogService.close();
						return true;
					}));
			}),
			catchError((err) => {
				this.dialogService.close();
				return of(false);
			})
		);
	}

	private setFamilyName(cmsTableData: TableData[]): void {
		cmsTableData?.forEach(cellData => {
			cellData?.cells?.forEach(cells => {
				const modelExternalName = cells.find(cell => cell.config.id === 'modelExternalNameMapId')?.formattedValue;
				if (!!modelExternalName) {
					const familyNameCell = cells.find(cell => cell.config.id === 'fn');
					familyNameCell.formattedValue = modelExternalName;
					familyNameCell.rawValue = modelExternalName;
				}
			});
		});
	}

	private combinedCellsData(modelCellsData: CellData[], holdingsCellsData: CellData[][], familyExcelSheetName: string): CellData[][] {
		holdingsCellsData.forEach((row, index) => {
			holdingsCellsData[index] = row.concat(modelCellsData, new CellData({ id: 'fsn', visible: true } as any, familyExcelSheetName)).filter(item => item?.config?.visible);
		});
		return holdingsCellsData;
	}

	private getFamilyExcelSheetName(modelCellsData: CellData[][]): string {
		// excel sheet name only allows 32 character
		const maximumSheetNameLength = 31;

		const familyFullName: string = modelCellsData.find(model => !!model[1].rawValue)?.[1].rawValue as string;
		// shorten version of family name can be overlapped if the first 28 characters are the same, hence, 2 version for fallback
		const proposedShortName = familyFullName.substring(0, maximumSheetNameLength - 3) + '...';
		const proposedShortNameIfOverlapped = this.getAlternativeFamilyExcelSheetName(familyFullName, proposedShortName);
		// check if name is already in used
		const usableShortName = this.allSheetNames.get(proposedShortName) ? proposedShortNameIfOverlapped : proposedShortName;
		// decide the sheet name, if not exceed the maximum limit, use name as is, otherwise, use one of the short name
		const familyFinalName = familyFullName.length > maximumSheetNameLength ? usableShortName : familyFullName;
		this.allSheetNames.set(familyFinalName, true);
		return familyFinalName;
	}

	private getAlternativeFamilyExcelSheetName(familyFullName: string, startingName: string): string {
		let name = startingName;
		let prefixCharacterCount = 23;
		let suffixCharacterCount = 5;

		while (this.allSheetNames.get(name) && prefixCharacterCount > 0) {
			name = familyFullName.substring(0, prefixCharacterCount) +
				'...' +
				familyFullName.substring(familyFullName.length - suffixCharacterCount, familyFullName.length);
			prefixCharacterCount--;
			suffixCharacterCount++;
			if (prefixCharacterCount === 0) {
				console.error('Models Holding Excel Error: Unavailable short name for:', familyFullName);
			}
		}

		return name;
	}

	private sortFamilyTableData(familyTableData: TableData[], familyId: string): TableData[] {
		return familyTableData.sort((table1, table2) => {
			// selected family first
			if (table1['rows'][0]['fid'].rawValue === familyId) {
				return -1;
			}

			if (table2['rows'][0]['fid'].rawValue === familyId) {
				return 1;
			}

			if (table1['rows'][0]['g'].rawValue !== table2['rows'][0]['g'].rawValue) {
				// custom first then BLK
				return ['CMS', 'BLK'].indexOf(<string>table1['rows'][0]['g'].rawValue) - ['CMS', 'BLK'].indexOf(<string>table2['rows'][0]['g'].rawValue);
			}
			// sort by display order in the same group
			return Number(table1['rows'][0]['do'].rawValue) - Number(table2['rows'][0]['do'].rawValue);
		});

	}
}
